import useEventListener from '@use-it/event-listener';
import type { ComponentChildren, FunctionComponent, RefObject, TargetedEvent } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { AutoFocusInside } from 'react-focus-lock';

import type { TemplateFunction } from 'Components/types';
import { setAutocompleteQuery } from 'Core/actions/autocomplete';
import { autocompleteRequestQuerySelector } from 'Core/selectors/autocomplete';
import { useDispatch, useSelector } from 'Core/store';
import isTruthy from 'Utils/isTruthy';

const AutoFocusInsideCompat = AutoFocusInside as unknown as FunctionComponent<{
  children?: ComponentChildren;
}>;

export type Params = {
  name: string;
};

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  rootRef: RefObject<HTMLElement>;
};

const SearchInput: FunctionComponent<Props> = ({ template, name, onChange, onFocus, rootRef }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const query = useSelector(autocompleteRequestQuerySelector);

  // hide keyboard on mobile when we scroll dropdown
  useEventListener(
    'touchmove',
    useCallback(() => inputRef.current?.blur(), []),
    rootRef.current?.querySelector<HTMLElement>('.cm_autocomplete'),
  );

  const component = template.call({ name });
  if (!component) {
    return null;
  }

  const { className: templateClassName, children: templateChildren, ...templateProps } = component.props;

  if (typeof templateChildren !== 'string' && templateChildren !== undefined) {
    console.error(
      'searchInput children expected to be either a string or nothing. Received:',
      templateChildren,
    );
    return null;
  }

  const props = {
    type: 'text',
    autoComplete: 'off',
    ...templateProps,
    className: [templateClassName, 'nav-root'].filter(isTruthy).join(' '),
    children: null,
    placeholder: templateChildren,
    value: query,
    onChange: (e: TargetedEvent<HTMLInputElement, Event>) => {
      const { value } = e.currentTarget;
      dispatch(setAutocompleteQuery(value));
      onChange(value);
    },
    onFocus,
    ref: inputRef,
  };

  if (name === 'SearchBoxDialog') {
    return (
      <AutoFocusInsideCompat>
        <input {...props} />
      </AutoFocusInsideCompat>
    );
  }

  return <input {...props} />;
};

export default SearchInput;
